import React, { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import { WorkspacesTableRowDefinition } from "../types";
import { WorkspaceVisibility } from "../../../types/workspace";

interface VisibilityDialogProps {
  isOpen: boolean;
  isLoading?: boolean;
  workspace?: WorkspacesTableRowDefinition;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: () => void;
}

export const VisibilityDialog: React.FC<VisibilityDialogProps> = (props) => {
  const isSettingPublic =
    props.workspace?.visibilityStatus === WorkspaceVisibility.PRIVATE;

  const dialogTitle = isSettingPublic
    ? "Make Project Public?"
    : "Make Project Private?";
  const buttonText = useMemo(
    () =>
      props.workspace?.id && isSettingPublic ? "Make Public" : "Make Private",
    [props.workspace?.id, isSettingPublic]
  );

  const bulletPoints = isSettingPublic
    ? [
        "View the project and its contents",
        "Create their own copy of the project",
        "Other users will not be able to modify the original project",
      ]
    : [
        "Hide the project from other users",
        "Remove access for users who previously could view it",
        "Prevent others from creating copies of this project",
      ];

  if (!props.isOpen) return null;

  return (
    <Dialog
      data-testid="workspace-visibility-dialog"
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth="sm"
      disableRestoreFocus
      fullWidth
    >
      <DialogTitle
        id="visibility-dialog-title"
        fontSize="20px"
        fontWeight={300}
      >
        <Box
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          width="94%"
          py={2}
        >
          {dialogTitle}{" "}
          <Tooltip arrow title={props.workspace?.name || ""}>
            <span style={{ fontWeight: "bold" }}>
              {props.workspace?.name || ""}
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>
      <IconButton
        data-testid="workspace-visibility-dialog-close-btn"
        onClick={() => props.setIsOpen(false)}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 26,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Divider />
      <DialogContent>
        <Stack spacing={2}>
          <Box id="visibility-dialog-description">
            <Stack spacing={2} alignItems="center">
              <Stack spacing={1.5} alignItems="flex-start" width="100%">
                <Typography fontSize="14px" color="text.secondary">
                  {isSettingPublic
                    ? "Making this project public will allow other users to:"
                    : "Making this project private will:"}
                </Typography>
                <Stack width="100%" pl={2}>
                  {bulletPoints.map((text, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                        }}
                      />
                      <Typography fontSize="14px" color="text.secondary">
                        {text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Typography fontSize="14px" color="text.secondary">
                  Are you sure you want to make this project{" "}
                  {isSettingPublic ? "public" : "private"}?
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 3 }}>
        <Button
          data-testid="workspace-visibility-dialog-cancel-btn"
          onClick={() => props.setIsOpen(false)}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "uppercase", minWidth: "100px" }}
        >
          Cancel
        </Button>
        <Button
          data-testid="workspace-visibility-dialog-confirm-btn"
          onClick={props.onConfirm}
          color="primary"
          variant="outlined"
          disabled={props.isLoading}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
